import { create } from 'zustand';
import {
  listDoctorRates,
  setDoctorRate,
  deleteDoctorRate,
} from '../services/api/procedureApi';

/**
 * Doctor Rate Overrides Store (Zustand)
 *
 * Holds the rate-override state for ONE selected doctor at a time. Rates
 * are keyed by procedure_id so the Doctor Rate Overrides page can render a
 * big list of catalog procedures with each row showing whether an override
 * exists.
 */
const useDoctorRateStore = create((set, get) => ({
  selectedDoctorId: null,
  rates: {},              // { [procedure_id]: { override_cost, created_at, updated_at } }
  isLoading: false,
  error: null,

  // ── Selection ───────────────────────────────────────────────────
  selectDoctor: async (doctorId) => {
    if (!doctorId) {
      set({ selectedDoctorId: null, rates: {}, error: null });
      return;
    }
    set({ selectedDoctorId: doctorId, isLoading: true, error: null, rates: {} });
    const result = await listDoctorRates(doctorId);
    if (result.success) {
      const byId = {};
      for (const r of (result.data.rates || [])) {
        byId[r.procedure_id] = r;
      }
      set({ rates: byId, isLoading: false });
    } else {
      set({ error: result.error, isLoading: false });
    }
  },

  // ── Upsert (called from the inline input, debounced in the page) ─
  setRate: async (procedureId, overrideCost) => {
    const { selectedDoctorId } = get();
    if (!selectedDoctorId) return { success: false, error: 'No doctor selected' };

    const result = await setDoctorRate(selectedDoctorId, procedureId, overrideCost);
    if (result.success && result.data) {
      set((s) => ({ rates: { ...s.rates, [procedureId]: result.data } }));
    }
    return result;
  },

  // ── Clear override (falls back to clinic default) ────────────────
  clearRate: async (procedureId) => {
    const { selectedDoctorId } = get();
    if (!selectedDoctorId) return { success: false, error: 'No doctor selected' };

    const result = await deleteDoctorRate(selectedDoctorId, procedureId);
    if (result.success) {
      set((s) => {
        const next = { ...s.rates };
        delete next[procedureId];
        return { rates: next };
      });
    }
    return result;
  },

  clearError: () => set({ error: null }),
}));

export default useDoctorRateStore;
